import { useRenderedCodeOptions } from 'ts/commons/code/RenderedCode.context';
import type { LineSegment } from 'ts/commons/code/RenderedCode.segments';
import { createStore, useStore } from 'zustand';

/**
 * State that describes the token/group that is currently highlighted (i.e., hovered with the mouse) which is stored in
 * a zustand store.
 */
export type HighlightedCodeTokenState = {
	/**
	 * Stores the {@link SourceFormatterContext#makeIdForFileAndOffset() fileAndOffsetId} of the token that is currently
	 * hovered.<p> If no token is hovered, it contains the empty string.
	 */
	hoveredId: string;

	expandedExpansionGroupIds: Set<number>;
	/**
	 * Stores the id of the expansion group which is currently highlighted. An expansion group is highlighted iff one
	 * token which is part of the expansion group is hovered.<p> If no expansion group is highlighted, it contains
	 * undefined.
	 *
	 * @see #hoveredId
	 */
	highlightedExpansionGroupId: number | undefined;
	/** Setter for the {@link #highlightedExpansionGroupId} */
	toggleGroupExpansion: (groupId: number | undefined) => void;
};

/** Stores which token/group currently are highlighted/expanded. */
export const createHighlightedCodeTokenStore = () =>
	createStore<HighlightedCodeTokenState>((set, get) => ({
		hoveredId: '',
		expandedExpansionGroupIds: new Set(),
		highlightedExpansionGroupId: undefined,
		toggleGroupExpansion: (groupId: number | undefined) => {
			if (groupId === undefined) {
				return;
			}
			const updatedExpansionGroups = new Set(get().expandedExpansionGroupIds);
			if (!updatedExpansionGroups.delete(groupId)) {
				updatedExpansionGroups.add(groupId);
			}
			set({ expandedExpansionGroupIds: updatedExpansionGroups });
		}
	}));

/**
 * Handles the hover/highlight state of a code token. It returns whether the token is currently hovered or the group it
 * is contained in is hovered. It also exposes functions to mark either the single token or a whole group as
 * highlighted(=hovered) and allows to manage the expansion state of a group.
 */
export function useCodeTokenHighlighting(
	segment: LineSegment,
	expansionGroupId: number | undefined
): {
	isHovered: boolean;
	handleMouseEnter: (() => void) | undefined;
	handleMouseLeave: (() => void) | undefined;
	toggleGroupExpansion: () => void;
	isExpandedExpansionGroup: boolean;
	isExpansionGroupHovered: boolean;
} {
	const { highlightedCodeTokenStore, hideOutline } = useRenderedCodeOptions();
	const tokenId = segment.fileAndOffsetId;
	const isHovered = useStore(highlightedCodeTokenStore, state => state.hoveredId === tokenId);
	const isExpandedExpansionGroup = useStore(
		highlightedCodeTokenStore,
		state => expansionGroupId != null && state.expandedExpansionGroupIds.has(expansionGroupId)
	);
	const isHighlightedExpansionGroupId = useStore(
		highlightedCodeTokenStore,
		state => expansionGroupId === state.highlightedExpansionGroupId
	);

	let handleMouseEnter: (() => void) | undefined;
	let handleMouseLeave: (() => void) | undefined;
	if (!hideOutline) {
		handleMouseEnter = () =>
			highlightedCodeTokenStore.setState({
				hoveredId: tokenId,
				highlightedExpansionGroupId: expansionGroupId
			});
		handleMouseLeave = () =>
			highlightedCodeTokenStore.setState({
				hoveredId: '',
				highlightedExpansionGroupId: undefined
			});
	}
	return {
		isHovered,
		handleMouseEnter,
		handleMouseLeave,
		toggleGroupExpansion: () => {
			highlightedCodeTokenStore.getState().toggleGroupExpansion(expansionGroupId);
		},
		isExpandedExpansionGroup,
		isExpansionGroupHovered: expansionGroupId !== undefined && isHighlightedExpansionGroupId
	};
}
